import amplitude from 'amplitude-js';
import { PAPEvent } from '@dropbox/pap-events/base/event';
import { IntegrationSource } from '@dropbox/pap-events/enums/integration_source';
import { isProduction, snakeCaseObj } from 'js/sign-components/common/ts-utils';
import { getPAPUser } from 'js/sign-components/common/pap-user-store';
import parseDomNodeContents from 'common/utils/parse-dom-node-contents';

type AmplitudeUserId = string | number | null | undefined;
type ConsentCategories = Record<string, boolean>;
type PAPAmplitudeClient = ReturnType<typeof amplitude.getInstance> & {
  sendEvents: () => void;
};

const AMPLITUDE_KEY = isProduction()
  ? '233ffee62e411e84d1807b55c77e9087'
  : '36f19c68dee21f264e20d9db589114df';
const API_ENDPOINT = 'www.dropbox.com/amplitude_proxy/ingest_lenient';
const CONSENT_COOKIE = '__Secure-dbx_consent';
const AMPLITUDE_COOKIE_PREFIX = `amp_${AMPLITUDE_KEY.slice(0, 6)}`;

let papClient: PAPAmplitudeClient | undefined;
let consentListenerInitialized = false;

function isPAPAnalyticsConsentGateEnabled(): boolean {
  try {
    const runtimeEnvironment = parseDomNodeContents('hs-env') as {
      papAnalyticsConsentGateEnabled?: boolean;
    };
    return runtimeEnvironment.papAnalyticsConsentGateEnabled === true;
  } catch {
    return false;
  }
}

function isGPCEnabled(): boolean {
  return Boolean(
    (navigator as Navigator & { globalPrivacyControl?: boolean })
      .globalPrivacyControl,
  );
}

function readAnalyticsConsentCookie(): boolean {
  try {
    const consentCookie = document.cookie
      .split(';')
      .map((cookie) => cookie.trim())
      .find((cookie) => cookie.startsWith(`${CONSENT_COOKIE}=`));

    if (!consentCookie) {
      return false;
    }

    const cookieValue = consentCookie.slice(consentCookie.indexOf('=') + 1);
    const consent = JSON.parse(cookieValue) as {
      categories?: ConsentCategories;
    };

    return Boolean(consent.categories?.all || consent.categories?.analytics);
  } catch {
    return false;
  }
}

function hasAnalyticsConsent(): boolean {
  return !isGPCEnabled() && readAnalyticsConsentCookie();
}

function expireCookie(name: string): void {
  const hostnameParts = window.location.hostname.split('.');
  const domains = hostnameParts.map((_, index) =>
    hostnameParts.slice(index).join('.'),
  );

  document.cookie = `${name}=; Max-Age=0; Path=/`;
  domains.forEach((domain) => {
    document.cookie = `${name}=; Max-Age=0; Path=/; Domain=${domain}`;
  });
}

function clearPAPClientCookies(): void {
  try {
    document.cookie
      .split(';')
      .map((cookie) => cookie.trim().split('=')[0])
      .filter((cookieName) => cookieName.startsWith(AMPLITUDE_COOKIE_PREFIX))
      .forEach(expireCookie);
  } catch {
    // Cookie access may be unavailable in privacy-focused configurations.
  }
}

function disablePAPClient(): void {
  if (papClient && !papClient.options.optOut) {
    papClient.setOptOut(true);
    // setOptOut prevents this call from transmitting and removes queued events.
    papClient.sendEvents();
  }

  clearPAPClientCookies();
}

function enableInitializedPAPClient(): void {
  if (!papClient?.options.optOut) {
    return;
  }

  papClient.setOptOut(false);
}

function handleAnalyticsConsentChange(): void {
  if (!isPAPAnalyticsConsentGateEnabled()) {
    return;
  }

  if (!hasAnalyticsConsent()) {
    disablePAPClient();
    return;
  }

  enableInitializedPAPClient();
}

export function initializePAPConsentHandling(): void {
  if (consentListenerInitialized || !isPAPAnalyticsConsentGateEnabled()) {
    return;
  }

  window.addEventListener(
    'dropbox:privacy-consent-changed',
    handleAnalyticsConsentChange,
  );
  consentListenerInitialized = true;
  handleAnalyticsConsentChange();
}

export function initPAPClient() {
  if (isPAPAnalyticsConsentGateEnabled()) {
    return undefined;
  }

  amplitude.getInstance().init(AMPLITUDE_KEY, undefined, {
    apiEndpoint: API_ENDPOINT,
  });

  return amplitude;
}

function initPAPClientV2() {
  if (!hasAnalyticsConsent()) {
    disablePAPClient();
    return undefined;
  }

  if (!papClient) {
    papClient = amplitude.getInstance() as PAPAmplitudeClient;
    papClient.init(AMPLITUDE_KEY, undefined, {
      apiEndpoint: API_ENDPOINT,
    });
    // A stale SDK opt-out value must not suppress events after fresh consent.
    papClient.setOptOut(false);
  } else {
    enableInitializedPAPClient();
  }

  return papClient;
}

export const logPAPEvent = (
  event: PAPEvent,
  userId: AmplitudeUserId = undefined,
  foreignUserId: AmplitudeUserId = undefined,
  userProperties: object = {},
) => {
  if (!isPAPAnalyticsConsentGateEnabled()) {
    const papUser = getPAPUser();
    const resolvedUserId = userId !== undefined ? userId : papUser?.dbxUserId;
    const resolvedForeignUserId =
      foreignUserId !== undefined ? foreignUserId : papUser?.guid;

    return initPAPClient()
      ?.getInstance()
      .logEvent(`${event.action}.${event.object}`, {
        user_properties: userProperties,
        event_action: event.action,
        event_object: event.object,
        event_class: event.class,
        user_identifier: {
          user_id: resolvedUserId,
          foreign_user_id: resolvedForeignUserId,
          // Dropbox is expecting a literal HELLOSIGN value for this API request
          // eslint-disable-next-line no-restricted-syntax
          foreign_user_provider: 'HELLOSIGN',
        },
        ...(event.properties ? snakeCaseObj(event.properties) : {}),
      });
  }

  initializePAPConsentHandling();

  const papClient = initPAPClientV2();
  if (!papClient) {
    // Pre-consent PAP is intentionally disabled rather than queued or replayed.
    return undefined;
  }

  const papUser = getPAPUser();
  const resolvedUserId = userId !== undefined ? userId : papUser?.dbxUserId;
  const resolvedForeignUserId =
    foreignUserId !== undefined ? foreignUserId : papUser?.guid;

  return papClient.logEvent(`${event.action}.${event.object}`, {
    user_properties: userProperties,
    event_action: event.action,
    event_object: event.object,
    event_class: event.class,
    user_identifier: {
      user_id: resolvedUserId,
      foreign_user_id: resolvedForeignUserId,
      // Dropbox is expecting a literal HELLOSIGN value for this API request
      // eslint-disable-next-line no-restricted-syntax
      foreign_user_provider: 'HELLOSIGN',
    },
    ...(event.properties ? snakeCaseObj(event.properties) : {}),
  });
};

export const integrationNames = {
  DeepIntegration: 'deepintegration',
  OfficeSelfSign: 'officeAddInSelfSign',
  HubSpot: 'hubspot',
  HubSpotV2: 'hubspotv2',
} as const;
export type IntegrationNames =
  (typeof integrationNames)[keyof typeof integrationNames];

export function retrieveFormattedIntegrationSource(
  integrationName: string,
): IntegrationSource {
  switch (integrationName) {
    case integrationNames.OfficeSelfSign:
      return 'office_add_in';
    case integrationNames.DeepIntegration:
      return 'deep_integration';
    case integrationNames.HubSpot:
      return 'hubspot';
    case integrationNames.HubSpotV2:
      return 'hubspotv2';
    default:
      return 'standalone';
  }
}
